/**
 * Grade — mirrors apps/experts/public/admin/grade.html. POST /api/admin/grade
 * with { scenario, response, model } returns a structured deep-grade critique
 * (verdict table, critical errors, structural issues, fixes). This is the page
 * whose ORIGINAL admin check failed OPEN (rebuild-lessons.md #2) — here the
 * single fail-closed AdminGuard wraps the whole admin subtree, so there is no
 * per-page gate to get wrong.
 */
import { useState } from 'react';
import { Button, Textarea } from '@andavo/web-ui';
import {
  AdminError,
  AdminPageHeader,
  Mono,
  Panel,
  Table,
  Td,
  Th,
  Tr,
} from '../../components/primitives';
import { useGrade } from '../../data-access/useAdmin';
import type { Grade } from '../../types';

const MODELS = ['gpt-4o', 'gpt-4o-mini', 'o1', 'o1-mini'];
const SEVERITY_CLASS: Record<string, string> = {
  hard_fail: 'text-red-600',
  major: 'text-amber-700',
  moderate: 'text-content-secondary',
};

function GradeResult({ grade }: { grade: Grade }) {
  return (
    <div className="flex flex-col gap-4">
      <Panel title="Verdict">
        <div className="mb-3 flex flex-wrap items-center gap-4 font-inter text-sm">
          <span className="font-sans font-semibold text-content-primary text-lg">
            {grade.overall_score?.toFixed(1)} / 10
          </span>
          <span
            className={
              grade.would_pass_adm_dispute ? 'text-green-600' : 'text-red-600'
            }
          >
            {grade.would_pass_adm_dispute
              ? 'Would pass ADM dispute'
              : 'Would NOT pass ADM dispute'}
          </span>
        </div>
        <Table
          head={
            <>
              <Th>Category</Th>
              <Th>Score</Th>
            </>
          }
        >
          {Object.entries(grade.verdict_table ?? {}).map(([cat, score]) => (
            <Tr key={cat}>
              <Td>{cat}</Td>
              <Td>{score}</Td>
            </Tr>
          ))}
        </Table>
      </Panel>

      {grade.critical_errors?.length ? (
        <Panel title="Critical errors">
          <ul className="flex flex-col gap-3">
            {grade.critical_errors.map((err) => (
              <li className="font-inter text-sm" key={err.id}>
                <p className="font-medium text-content-primary">
                  {err.id}. {err.title}{' '}
                  <span className={SEVERITY_CLASS[err.severity] ?? ''}>
                    [{err.severity}]
                  </span>
                </p>
                <p className="mt-0.5 text-content-secondary">
                  Wrong: {err.violation}
                </p>
                <p className="text-content-secondary">Correct: {err.correct}</p>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      {grade.structural_issues?.length ? (
        <Panel title="Structural issues">
          <ul className="flex flex-col gap-3">
            {grade.structural_issues.map((iss) => (
              <li className="font-inter text-sm" key={iss.id}>
                <p className="font-medium text-content-primary">
                  {iss.id}. {iss.title}
                </p>
                <p className="mt-0.5 text-content-secondary">
                  Missing: {iss.missing}
                </p>
                <p className="text-content-secondary">Impact: {iss.impact}</p>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}

      <Panel title="Top 3 fixes">
        <ol className="list-inside list-decimal font-inter text-content-primary text-sm">
          {(grade.top_3_fixes ?? []).map((fix) => (
            <li key={fix}>{fix}</li>
          ))}
        </ol>
      </Panel>

      {grade._validation_errors?.length ? (
        <Panel title="Validation warnings">
          <ul className="font-inter text-amber-700 text-xs">
            {grade._validation_errors.map((v) => (
              <li key={v}>
                <Mono>{v}</Mono>
              </li>
            ))}
          </ul>
        </Panel>
      ) : null}
    </div>
  );
}

export function GradePage() {
  const grade = useGrade();
  const [scenario, setScenario] = useState('');
  const [response, setResponse] = useState('');
  const [model, setModel] = useState(MODELS[0]);

  const canSubmit = scenario.trim() && response.trim() && !grade.isPending;

  return (
    <div>
      <AdminPageHeader
        subtitle="Deep-grade an advisor response against ATPCO/Sabre reissue rules."
        title="Grade"
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Input">
          <form
            className="flex flex-col gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (!canSubmit) return;
              grade.mutate({
                scenario: scenario.trim(),
                response: response.trim(),
                model,
              });
            }}
          >
            <label className="flex flex-col gap-1 font-inter text-content-secondary text-xs">
              Scenario
              <Textarea
                className="min-h-32 text-sm"
                maxLength={8000}
                onChange={(e) => setScenario(e.target.value)}
                placeholder="Paste the reissue/refund scenario…"
                value={scenario}
              />
            </label>
            <label className="flex flex-col gap-1 font-inter text-content-secondary text-xs">
              Advisor response
              <Textarea
                className="min-h-48 text-sm"
                maxLength={12000}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Paste the advisor's response to grade…"
                value={response}
              />
            </label>
            <label className="flex flex-col gap-1 font-inter text-content-secondary text-xs">
              Model
              <select
                className="w-40 rounded-md border border-border-subtle bg-surface-background px-2 py-1.5 font-inter text-content-primary text-sm"
                onChange={(e) => setModel(e.target.value)}
                value={model}
              >
                {MODELS.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </label>
            <div>
              <Button disabled={!canSubmit} type="submit">
                {grade.isPending ? 'Grading…' : 'Grade'}
              </Button>
            </div>
            {grade.isError ? (
              <AdminError
                message={`Grade failed: ${(grade.error as Error)?.message ?? 'unknown'}`}
              />
            ) : null}
          </form>
        </Panel>

        <div>
          {grade.isPending ? (
            <Panel>
              <p className="font-inter text-content-secondary text-sm">
                Deep-grading (this can take ~30s)…
              </p>
            </Panel>
          ) : null}
          {grade.isSuccess ? <GradeResult grade={grade.data.grade} /> : null}
          {!grade.isPending && !grade.isSuccess ? (
            <Panel>
              <p className="font-inter text-content-tertiary text-sm">
                Submit a scenario and response to see the structured critique.
              </p>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default GradePage;
